// src/shared/screens/auth/LoginView.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../../i18n/I18nProvider';
import MobileInputModal from '../../components/MobileInputModal';
import PwaInstallControl from '../../components/PwaInstallControl';
import { resolveAuthErrorMessage } from '../../utils/systemMessage';

/**
 * LoginView (Base: 375x812)
 * - public/assets:
 *   - /assets/login_bg.png
 *   - /assets/logo.svg
 * - Design updates:
 *   1) 모든 텍스트 사이즈 절반(기본 16 -> 8)
 *   2) 입력/버튼 라운드 3px
 *   3) 버튼 hover/active 네온 효과(테두리색 기준)
 *
 * ✅ Fix:
 * - onSubmit만 호출하고 끝나서 "로그인 후 로비 이동"이 안 되는 문제 해결
 * - onSubmit 이후 localStorage 토큰 존재 여부로 성공 판단 후 /lobby 이동
 * - 서버가 id를 기대하는 케이스 대비해서 { id: username }도 함께 전달
 */
export default function LoginView({
  logoUrl,
  onSubmit,
  onAccountRecovery,
  onContactAgent,
  onCreateAccount,
  loading = false,
  useMobileInputModal = false,
  mobileFontBoost = 0,

  // ✅ 추가 옵션 (필요하면 부모에서 덮어쓰기)
  lobbyPath = '/lobby',
  autoNavigateOnSuccess = true,

  // ✅ 토큰 키 후보(프로젝트별로 다를 수 있어서 후보로 검사)
  tokenKeys = ['token', 'accessToken', 'access_token', 'jwt', 'authToken'],
  renderVideoBackground = true,
}) {
  // [PATCH][ANCHOR:LOGINVIEW:I18N_HOOK_WITH_SETLANG]
  const { t, lang, setLang } =
    useI18n?.() || { t: (k) => k, lang: 'en', setLang: () => {} };

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [autoLogin, setAutoLogin] = useState(false);

  const [localBusy, setLocalBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const logo = useMemo(() => logoUrl || '/assets/logo.svg', [logoUrl]);

  // ✅ 텍스트 사이즈 절반: 기본 16px -> 8px
  const baseFontSize = 8 + Number(mobileFontBoost || 0);

  const disabledCommonStyle = (loading || localBusy)
    ? { opacity: 0.6, cursor: 'not-allowed' }
    : undefined;

  const hasAnyToken = () => {
    try {
      for (const k of tokenKeys) {
        const v = localStorage.getItem(k);
        if (v && String(v).trim().length > 0) return true;
      }
    } catch (_) {}
    return false;
  };

  const goLobby = () => {
    try {
      // SPA navigate가 아니라도 확실히 보내기 위해 replace 사용
      window.location.replace(lobbyPath);
    } catch (_) {
      window.location.href = lobbyPath;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || localBusy) return;

    setErrorMsg('');

    if (!username.trim()) {
      setErrorMsg(t('auth.login.error.idRequired', null, 'ID를 입력해주세요.'));
      return;
    }
    if (!password) {
      setErrorMsg(t('auth.login.error.passwordRequired', null, '비밀번호를 입력해주세요.'));
      return;
    }

    setLocalBusy(true);

    try {
      // ✅ 서버가 id를 기대하는 케이스 대비해서 둘 다 전달
      const payload = {
        username,
        id: username,
        password,
        autoLogin,
      };

      // onSubmit이 Promise를 반환해도/안 해도 대응
      const ret = onSubmit?.(payload);
      if (ret && typeof ret.then === 'function') {
        await ret;
      }

      // ✅ 성공 판단:
      // 1) onSubmit 내부에서 토큰 저장하는 구조라면 localStorage에 토큰이 생김
      // 2) 토큰 존재 시 로비로 이동
      if (autoNavigateOnSuccess && hasAnyToken()) {
        goLobby();
        return;
      }

      // 토큰이 없으면 실패 가능성이 높으니 사용자에게 힌트 제공
      // (부모에서 에러처리를 안 해도 여기서 최소한 표시)
      if (autoNavigateOnSuccess) {
        setErrorMsg(t('auth.login.error.tokenNotStored', null, '로그인에 실패했거나 토큰 저장이 되지 않았습니다.'));
      }
    } catch (err) {
      const msg = resolveAuthErrorMessage(
        t,
        err,
        t('auth.login.error.failed', null, 'Login failed.')
      );
      setErrorMsg(String(msg));
      // eslint-disable-next-line no-console
      console.error('[LoginView] submit error:', err);
    } finally {
      setLocalBusy(false);
    }
  };

  return (
    <div
      style={{
        width: 375,
        height: 812,
        position: 'relative',
        background: renderVideoBackground ? '#000000' : 'transparent',
        overflow: 'hidden',
        fontSize: baseFontSize,
        lineHeight: 1.2,
      }}
    >
      {/* 버튼 네온 CSS (hover/active는 inline로 못하므로 style tag로) */}
      <style>{`
        .npc-btn {
          border-radius: 3px;
          transition: box-shadow .18s ease, border-color .18s ease, transform .12s ease;
          outline: none;
        }
        .npc-btn:disabled {
          box-shadow: none !important;
          transform: none !important;
        }

        /* ORANGE (Sign in) */
        .npc-orange:hover {
          border-color: #FFD27D !important;
          box-shadow:
            0 0 3px rgba(255,167,45,.70),
            0 0 8px rgba(255,167,45,.45),
            0 0 14px rgba(255,167,45,.25);
        }
        .npc-orange:active {
          transform: scale(.97);
          box-shadow:
            0 0 2px rgba(255,167,45,.90),
            0 0 6px rgba(255,167,45,.60);
        }

        /* GREEN (Agent/New) */
        .npc-green:hover {
          border-color: #7CFFC0 !important;
          box-shadow:
            0 0 3px rgba(45,255,125,.65),
            0 0 8px rgba(45,255,125,.40),
            0 0 14px rgba(45,255,125,.22);
        }
        .npc-green:active {
          transform: scale(.97);
          box-shadow:
            0 0 2px rgba(45,255,125,.85),
            0 0 6px rgba(45,255,125,.55);
        }

        /* Placeholder color */
        .npc-input::placeholder {
          color: rgba(255,255,255,0.45);
        }

        /* Login video background: same video + mesh feel as site.zip */
        .npc-login-video {
          position: absolute;
          inset: 0;
          z-index: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          background: #000;
        }
        .npc-login-video-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background:
            radial-gradient(circle at 52% 42%, rgba(255, 255, 255, 0.16), transparent 22%),
            linear-gradient(180deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.32) 52%, rgba(0, 0, 0, 0.72) 100%),
            linear-gradient(90deg, rgba(0, 0, 0, 0.54) 0%, rgba(0, 0, 0, 0.06) 45%, rgba(0, 0, 0, 0.58) 100%),
            rgba(0, 0, 0, 0.14);
        }
        .npc-login-video-mesh {
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
          background-image:
            linear-gradient(rgba(0, 0, 0, 0.16) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 0, 0, 0.16) 1px, transparent 1px);
          background-size: 3px 3px;
          opacity: 0.45;
          mix-blend-mode: multiply;
        }
      `}</style>

      {renderVideoBackground ? (
        <>
          <video
            aria-hidden="true"
            autoPlay
            className="npc-login-video"
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src="/assets/test.mp4" type="video/mp4" />
          </video>
          <div className="npc-login-video-overlay" aria-hidden="true" />
          <div className="npc-login-video-mesh" aria-hidden="true" />
        </>
      ) : null}

      <style>{`
          {/* [ADD][ANCHOR:LOGINVIEW:LANG_DROPDOWN_CSS] */}
        .npc-lang-wrap{
          position:absolute;
          right:10px;
          top:10px;
          z-index:50;
        }

        .npc-lang-btn{
          height:20px;
          min-width:120px;
          width:100%;                 /* ✅ wrap 폭에 맞춤 */
          padding:0 8px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:8px;
          background:rgba(0,0,0,0.35);
          border:1px solid rgba(255,255,255,0.35);
          border-radius:3px;
          color:#fff;
          cursor:pointer;
          box-sizing:border-box;
        }
        .npc-lang-btn:hover{ border-color:rgba(255,255,255,0.60); }
        .npc-lang-btn:disabled{ opacity:0.55; cursor:not-allowed; }

        .npc-lang-menu{
          margin-top:6px;
          width:100%;                 /* ✅ 버튼 폭과 동일 */
          max-height:330px;
          overflow:auto;
          background:rgba(0,0,0,0.75);
          border:1px solid rgba(255,255,255,0.25);
          border-radius:3px;
          box-shadow:0 10px 25px rgba(0,0,0,0.35);
        }

        /* ✅ 스크롤바: track 투명 / thumb #000 / radius 5 / width 3 */
        .npc-lang-menu{ scrollbar-color:#000 transparent; scrollbar-width:thin; } /* firefox */
        .npc-lang-menu::-webkit-scrollbar{ width:3px; }
        .npc-lang-menu::-webkit-scrollbar-track{ background:transparent; }
        .npc-lang-menu::-webkit-scrollbar-thumb{
          background:#000;
          border-radius:5px;
        }

        .npc-lang-item{
          width:100%;
          padding:6px 8px;
          display:flex;
          align-items:center;
          gap:8px;
          background:transparent;
          border:0;
          color:#fff;
          cursor:pointer;
          text-align:left;
          box-sizing:border-box;
        }
        .npc-lang-item:hover{ background:rgba(255,255,255,0.08); }
      `}</style>


      {/* Layer 1 Frame: 361x796, left 7, top 8 */}
      <div
        style={{
          position: 'absolute',
          left: 7,
          top: 8,
          width: 361,
          height: 796,
          background: 'transparent',
          border: 'none',
          boxSizing: 'border-box',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        

       {/* [ADD][ANCHOR:LOGINVIEW:LANGUAGE_COMBOBOX_TOPRIGHT] */}
       <LanguageComboBox
          value={String(lang || 'en')}
          onChange={(next) => setLang?.(next)}
          disabled={loading || localBusy}
          baseFontSize={baseFontSize}
          ariaLabel={t('common.language', null, 'language')}
        />

        {/* 13 */}
        <div style={{ height: 13 }} />

        {/* Title */}
        <div style={{ color: '#fff', fontSize: baseFontSize }}>
          NIGHT CLUB Network
        </div>

        {/* 54 */}
        <div style={{ height: 54 }} />

        {/* Logo 240x160 */}
        <div
          style={{
            width: 240,
            height: 160,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          <img
            src={logo}
            alt={t('common.logoAlt', null, 'logo')}
            style={{ width: 240, height: 160, objectFit: 'contain', display: 'block' }}
            draggable={false}
          />
        </div>

        {/* 54 */}
        <div style={{ height: 54 }} />

        <form
          onSubmit={handleSubmit}
          style={{
            width: 240,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontSize: baseFontSize,
          }}
        >
          {/* ID Label */}
          <div style={{ width: '100%', textAlign: 'center', color: '#fff', fontSize: baseFontSize }}>
            {t('auth.login.username.label')}
          </div>

          {/* 14 */}
          <div style={{ height: 14 }} />

          {/* ID Input 240x29 */}
          <InputBox
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t('auth.login.username.placeholder')}
            type="text"
            autoComplete="username"
            disabled={loading || localBusy}
            fontSize={baseFontSize}
            useMobileInputModal={useMobileInputModal}
            modalTitle={t('auth.login.username.inputModalTitle', null, '아이디 입력')}
            modalDescription={t('auth.login.username.inputModalGuide', null, '로그인할 아이디를 입력해주세요.')}
          />

          {/* 41 */}
          <div style={{ height: 41 }} />

          {/* PASSWORD Label */}
          <div style={{ width: '100%', textAlign: 'center', color: '#fff', fontSize: baseFontSize }}>
            {t('auth.login.password.label', null, 'PASSWORD')}
          </div>

          {/* 9 */}
          <div style={{ height: 9 }} />

          {/* PW Input 240x29 */}
          <InputBox
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.login.password.placeholder')}
            type="password"
            autoComplete="current-password"
            disabled={loading || localBusy}
            fontSize={baseFontSize}
            useMobileInputModal={useMobileInputModal}
            modalTitle={t('auth.login.password.inputModalTitle', null, '비밀번호 입력')}
            modalDescription={t('auth.login.password.inputModalGuide', null, '로그인 비밀번호를 입력해주세요.')}
          />

          {/* 32 */}
          <div style={{ height: 32 }} />

          {/* Sign in 240x32 */}
          <button
            type="submit"
            disabled={loading || localBusy}
            className="npc-btn npc-orange"
            style={{
              width: 240,
              height: 32,
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid #FFA72D',
              color: '#fff',
              boxSizing: 'border-box',
              fontSize: baseFontSize,
              borderRadius: 3,
              ...(disabledCommonStyle || { cursor: 'pointer' }),
            }}
          >
            {loading || localBusy ? t('auth.login.signingIn', null, 'Signing in…') : t('auth.login.submit', null, 'Sign in')}
          </button>

          {/* 에러 메시지 */}
          {errorMsg ? (
            <>
              <div style={{ height: 8 }} />
              <div
                style={{
                  width: 240,
                  textAlign: 'center',
                  color: '#ff5a5a',
                  fontSize: baseFontSize,
                  textShadow: '0 0 8px rgba(255,0,0,0.25)',
                  userSelect: 'none',
                }}
              >
                {errorMsg}
              </div>
            </>
          ) : null}

          {/* 10 */}
          <div style={{ height: 10 }} />

          {/* Agent/New row: 114 + 12 + 114 = 240 */}
          <div style={{ width: 240, display: 'flex', gap: 12 }}>
            <button
              type="button"
              disabled={loading || localBusy}
              className="npc-btn npc-green"
              style={{
                ...greenBtnStyle,
                fontSize: baseFontSize,
                borderRadius: 3,
                ...(disabledCommonStyle || { cursor: 'pointer' }),
              }}
              onClick={() => {
                if (loading || localBusy) return;
                const handler = onAccountRecovery || onContactAgent;
                handler?.({ username, id: username, autoLogin });
              }}
            >
              {t('auth.login.accountRecovery', null, '계정 찾기')}
            </button>

            <button
              type="button"
              disabled={loading || localBusy}
              className="npc-btn npc-green"
              style={{
                ...greenBtnStyle,
                fontSize: baseFontSize,
                borderRadius: 3,
                ...(disabledCommonStyle || { cursor: 'pointer' }),
              }}
              onClick={() => {
                if (loading || localBusy) return;
                onCreateAccount?.({ username, id: username });
              }}
            >
              {t('auth.login.createAccount')}
            </button>
          </div>

          {/* 12 */}
          <div style={{ height: 12 }} />

          {/* Auto log-in: checkbox + box (bg 50%, border #3c3c3c) */}
          <div
            style={{
              width: 240,
              height: 29,
              background: 'rgba(0,0,0,0.8)',
              border: '1px solid #3c3c3c',
              borderRadius: 3,
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px',
              gap: 10,
              color: '#fff',
              fontSize: baseFontSize,
              ...(loading || localBusy ? { opacity: 0.6 } : undefined),
            }}
          >
            <input
              type="checkbox"
              checked={autoLogin}
              disabled={loading || localBusy}
              onChange={(e) => setAutoLogin(e.target.checked)}
              style={{
                width: 12,
                height: 12,
                margin: 0,
                cursor: (loading || localBusy) ? 'not-allowed' : 'pointer',
              }}
            />
            <span style={{ userSelect: 'none' }}>{t('auth.login.autoSignIn')}</span>
          </div>

          <PwaInstallControl
            baseFontSize={baseFontSize}
            disabled={loading || localBusy}
          />
        </form>

        {/* 222 */}
        <div style={{ height: 222 }} />
      </div>
    </div>
  );
}

// [ADD][ANCHOR:LOGINVIEW:LANGUAGE_COMBOBOX_COMPONENT]
const NPC_LANG_ITEMS = [
  { flag: 'Bangladesh',       label: 'Bangladesh',       lang: 'bn' },
  { flag: 'Brazil',           label: 'Brazil',           lang: 'pt-br' },
  { flag: 'China',            label: 'China',            lang: 'zh-cn' },
  { flag: 'France',           label: 'France',           lang: 'fr' },
  { flag: 'Greece',           label: 'Greece',           lang: 'el' },
  { flag: 'India',            label: 'India',            lang: 'hi' },
  { flag: 'Indonesia',        label: 'Indonesia',        lang: 'id' },
  { flag: 'Italy',            label: 'Italy',            lang: 'it' },
  { flag: 'Japan',            label: 'Japan',            lang: 'ja' },
  { flag: 'Korea',            label: 'Korea',            lang: 'ko' },      // ✅ 현재 한글 JSON 즉시 적용
  { flag: 'Malaysia',         label: 'Malaysia',         lang: 'ms' },
  { flag: 'Mongolia',         label: 'Mongolia',         lang: 'mn' },
  { flag: 'Mexico',           label: 'Mexico',           lang: 'es-mx' },
  { flag: 'Norway',           label: 'Norway',           lang: 'nb-no' },
  { flag: 'Pakistan',         label: 'Pakistan',         lang: 'ur' },
  { flag: 'Philippines',      label: 'Philippines',      lang: 'fil' },
  { flag: 'Portugal',         label: 'Portugal',         lang: 'pt' },
  { flag: 'Russia',           label: 'Russia',           lang: 'ru' },
  { flag: 'Spain',            label: 'Spain',            lang: 'es' },
  { flag: 'Thailand',         label: 'Thailand',         lang: 'th' },
  { flag: 'Turkey',           label: 'Turkey',           lang: 'tr' },
  { flag: 'United_Kingdom',   label: 'United Kingdom',   lang: 'en-gb' },
  { flag: 'Vietnam',          label: 'Vietnam',          lang: 'vi' },
];

function LanguageComboBox({ value, onChange, disabled = false, baseFontSize = 8, ariaLabel = 'language' }) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  const current = useMemo(() => {
    const v = String(value || '').toLowerCase();
    return (
      NPC_LANG_ITEMS.find((x) => String(x.lang).toLowerCase() === v) ||
      NPC_LANG_ITEMS.find((x) => String(x.lang).toLowerCase() === 'en-gb') ||
      NPC_LANG_ITEMS[0]
    );
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const el = wrapRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className="npc-lang-wrap" ref={wrapRef}>
      <button
        type="button"
        className="npc-lang-btn"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        style={{ fontSize: baseFontSize }}
        aria-label={ariaLabel}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src={`/assets/flags/${current.flag}.svg`}
            alt=""
            style={{ width: 18, height: 12, display: 'block' }}
            draggable={false}
          />
          <span style={{ whiteSpace: 'nowrap', opacity: 0.95 }}>{current.label}</span>
        </span>
        <span style={{ opacity: 0.8 }}>▾</span>
      </button>

      {open ? (
        <div className="npc-lang-menu">
          {NPC_LANG_ITEMS.map((it) => {
            const selected = String(it.lang).toLowerCase() === String(value || '').toLowerCase();
            return (
              <button
                key={it.flag}
                type="button"
                className="npc-lang-item"
                onClick={() => {
                  onChange?.(it.lang);
                  setOpen(false);
                }}
                style={{ fontSize: baseFontSize }}
              >
                <img
                  src={`/assets/flags/${it.flag}.svg`}
                  alt=""
                  style={{ width: 18, height: 12, display: 'block' }}
                  draggable={false}
                />
                <span style={{ flex: 1 }}>{it.label}</span>
                {selected ? <span style={{ opacity: 0.9 }}>✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function InputBox({
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
  disabled = false,
  fontSize = 8,
  useMobileInputModal = false,
  modalTitle,
  modalDescription,
}) {
  return (
    <div
      style={{
        width: 240,
        height: 29,
        background: 'rgba(0,0,0,0.5)',
        border: '1px solid #3c3c3c',
        borderRadius: 3,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        padding: '0 10px',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {useMobileInputModal ? (
        <MobileInputModal
          className="npc-input"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          modalInputType={type === 'password' ? 'password' : 'text'}
          autoComplete={autoComplete}
          disabled={disabled}
          modalTitle={modalTitle}
          modalDescription={modalDescription}
          triggerAsButton
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: '#fff',
            padding: 0,
            fontSize,
            lineHeight: 1.2,
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
      ) : (
        <input
          className="npc-input"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          autoComplete={autoComplete}
          disabled={disabled}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: '#fff',
            padding: 0,
            fontSize,
            lineHeight: 1.2,
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
      )}
    </div>
  );
}

const greenBtnStyle = {
  width: 114,
  height: 32,
  background: 'rgba(0,0,0,0.5)',
  border: '1px solid #2DFF7D',
  color: '#fff',
  boxSizing: 'border-box',
};
